import Header from '@/components/Header';
import Footer from '@/components/Footer';
import LogoBigColor from '@/icons/LogoBigColor';
import MobileBottomNav from '@/components/MobileBottomNav';
import { IconArrowLeft as ArrowLeft } from '@tabler/icons-react';
import { PageHero, btnPrimary, pageBg, surface, wrap } from '@/components/ui/design';

export default function PrivacyPolicy() {
    return (
        <div className={`min-h-screen flex flex-col ${pageBg}`}>
            <Header />

            <main className="flex-1">
                <PageHero
                    eyebrow={<LogoBigColor width={160} height={45} />}
                    title="Privacy Policy"
                    subtitle={<>Your privacy is important to us. This policy explains how we collect, use, and protect your information when you use our platform.</>}
                >
                    <p className="mt-6 inline-flex h-9 items-center rounded-full bg-grey99 px-4 text-sm font-semibold text-grey40 ring-1 ring-black/[0.04] dark:bg-white/[0.06] dark:text-nightMuted dark:ring-white/10">
                        Last updated: September 2025
                    </p>
                </PageHero>

                <div className={`${wrap} mt-14 pb-20 sm:mt-20`}>
                    <article className="max-w-[720px]">
                        <section className="mb-14">
                            <h2 className="mb-5 text-[clamp(24px,3vw,30px)] font-bold leading-tight tracking-[-0.03em] text-grey10 dark:text-white">1. Introduction</h2>
                            <p className="mb-4 text-[17px] leading-[1.75] text-grey40 dark:text-nightMuted">
                                Welcome to {import.meta.env.VITE_SITE_NAME}, a global platform for discovering, creating, and promoting Iranian events.
                                We are committed to protecting your privacy and ensuring the security of your personal information.
                            </p>
                            <p className="text-[17px] leading-[1.75] text-grey40 dark:text-nightMuted">
                                This Privacy Policy describes how we collect, use, disclose, and safeguard your information
                                when you visit our website or use our services.
                            </p>
                        </section>

                        <section className="mb-14">
                            <h2 className="mb-5 text-[clamp(24px,3vw,30px)] font-bold leading-tight tracking-[-0.03em] text-grey10 dark:text-white">2. Information We Collect</h2>

                            <h3 className="mt-8 mb-3 text-lg font-bold tracking-[-0.02em] text-grey20 dark:text-nightTextSecondary">2.1 Personal Information</h3>
                            <p className="mb-4 text-[17px] leading-[1.75] text-grey40 dark:text-nightMuted">
                                We may collect personal information that you voluntarily provide to us, including:
                            </p>
                            <ul className="mb-4 list-disc space-y-2 pl-5 text-[17px] leading-[1.75] text-grey40 marker:text-grey70 dark:text-nightMuted dark:marker:text-white/30">
                                <li>Name and contact information (email address, phone number)</li>
                                <li>Account credentials and profile information</li>
                                <li>Payment and billing information</li>
                                <li>Event preferences and interests</li>
                                <li>Communications with us (support requests, feedback)</li>
                            </ul>

                            <h3 className="mt-8 mb-3 text-lg font-bold tracking-[-0.02em] text-grey20 dark:text-nightTextSecondary">2.2 Automatically Collected Information</h3>
                            <p className="mb-4 text-[17px] leading-[1.75] text-grey40 dark:text-nightMuted">
                                We automatically collect certain information when you use our services:
                            </p>
                            <ul className="mb-4 list-disc space-y-2 pl-5 text-[17px] leading-[1.75] text-grey40 marker:text-grey70 dark:text-nightMuted dark:marker:text-white/30">
                                <li>Device information (IP address, browser type, operating system)</li>
                                <li>Usage data (pages visited, time spent, features used)</li>
                                <li>Location data (with your permission)</li>
                                <li>Cookies and similar tracking technologies</li>
                            </ul>
                        </section>

                        <section className="mb-14">
                            <h2 className="mb-5 text-[clamp(24px,3vw,30px)] font-bold leading-tight tracking-[-0.03em] text-grey10 dark:text-white">3. How We Use Your Information</h2>
                            <p className="mb-4 text-[17px] leading-[1.75] text-grey40 dark:text-nightMuted">
                                We use the information we collect for various purposes, including:
                            </p>
                            <ul className="mb-4 list-disc space-y-2 pl-5 text-[17px] leading-[1.75] text-grey40 marker:text-grey70 dark:text-nightMuted dark:marker:text-white/30">
                                <li>Providing and maintaining our services</li>
                                <li>Processing transactions and managing your account</li>
                                <li>Personalizing your experience and recommendations</li>
                                <li>Communicating with you about events and updates</li>
                                <li>Improving our platform and developing new features</li>
                                <li>Ensuring security and preventing fraud</li>
                                <li>Complying with legal obligations</li>
                            </ul>
                        </section>

                        <section className="mb-14">
                            <h2 className="mb-5 text-[clamp(24px,3vw,30px)] font-bold leading-tight tracking-[-0.03em] text-grey10 dark:text-white">4. Information Sharing and Disclosure</h2>
                            <p className="mb-4 text-[17px] leading-[1.75] text-grey40 dark:text-nightMuted">
                                We do not sell, trade, or rent your personal information to third parties. We may share
                                your information in the following circumstances:
                            </p>
                            <ul className="mb-4 list-disc space-y-2 pl-5 text-[17px] leading-[1.75] text-grey40 marker:text-grey70 dark:text-nightMuted dark:marker:text-white/30">
                                <li>With event organizers when you purchase tickets</li>
                                <li>With service providers who assist us in operating our platform</li>
                                <li>When required by law or to protect our rights</li>
                                <li>In connection with a business transfer or merger</li>
                                <li>With your explicit consent</li>
                            </ul>
                        </section>

                        <section className="mb-14">
                            <h2 className="mb-5 text-[clamp(24px,3vw,30px)] font-bold leading-tight tracking-[-0.03em] text-grey10 dark:text-white">5. Data Security</h2>
                            <p className="mb-4 text-[17px] leading-[1.75] text-grey40 dark:text-nightMuted">
                                We implement appropriate technical and organizational measures to protect your personal
                                information against unauthorized access, alteration, disclosure, or destruction. These measures include:
                            </p>
                            <ul className="mb-4 list-disc space-y-2 pl-5 text-[17px] leading-[1.75] text-grey40 marker:text-grey70 dark:text-nightMuted dark:marker:text-white/30">
                                <li>Encryption of sensitive data in transit and at rest</li>
                                <li>Regular security assessments and updates</li>
                                <li>Access controls and authentication mechanisms</li>
                                <li>Employee training on data protection practices</li>
                            </ul>
                        </section>

                        <section className="mb-14">
                            <h2 className="mb-5 text-[clamp(24px,3vw,30px)] font-bold leading-tight tracking-[-0.03em] text-grey10 dark:text-white">6. Cookies and Tracking Technologies</h2>
                            <p className="mb-4 text-[17px] leading-[1.75] text-grey40 dark:text-nightMuted">
                                We use cookies and similar technologies to enhance your experience on our platform.
                                Cookies are small text files stored on your device that help us:
                            </p>
                            <ul className="mb-4 list-disc space-y-2 pl-5 text-[17px] leading-[1.75] text-grey40 marker:text-grey70 dark:text-nightMuted dark:marker:text-white/30">
                                <li>Remember your preferences and settings</li>
                                <li>Analyze how you use our services</li>
                                <li>Provide personalized content and advertisements</li>
                                <li>Improve our platform's functionality</li>
                            </ul>
                            <p className="text-[17px] leading-[1.75] text-grey40 dark:text-nightMuted">
                                You can control cookie settings through your browser preferences, though some features
                                may not function properly if cookies are disabled.
                            </p>
                        </section>

                        <section className="mb-14">
                            <h2 className="mb-5 text-[clamp(24px,3vw,30px)] font-bold leading-tight tracking-[-0.03em] text-grey10 dark:text-white">7. Your Rights and Choices</h2>
                            <p className="mb-4 text-[17px] leading-[1.75] text-grey40 dark:text-nightMuted">
                                Depending on your location, you may have certain rights regarding your personal information:
                            </p>
                            <ul className="mb-4 list-disc space-y-2 pl-5 text-[17px] leading-[1.75] text-grey40 marker:text-grey70 dark:text-nightMuted dark:marker:text-white/30">
                                <li>Access and review your personal information</li>
                                <li>Correct or update inaccurate information</li>
                                <li>Delete your personal information</li>
                                <li>Restrict or object to certain processing activities</li>
                                <li>Data portability (receive a copy of your data)</li>
                                <li>Withdraw consent where applicable</li>
                            </ul>
                            <p className="text-[17px] leading-[1.75] text-grey40 dark:text-nightMuted">
                                To exercise these rights, please contact us using the information provided in the
                                "Contact Us" section below.
                            </p>
                        </section>

                        <section className="mb-14">
                            <h2 className="mb-5 text-[clamp(24px,3vw,30px)] font-bold leading-tight tracking-[-0.03em] text-grey10 dark:text-white">8. Data Retention</h2>
                            <p className="text-[17px] leading-[1.75] text-grey40 dark:text-nightMuted">
                                We retain your personal information for as long as necessary to fulfill the purposes
                                outlined in this Privacy Policy, unless a longer retention period is required or
                                permitted by law. When we no longer need your information, we will securely delete
                                or anonymize it.
                            </p>
                        </section>

                        <section className="mb-14">
                            <h2 className="mb-5 text-[clamp(24px,3vw,30px)] font-bold leading-tight tracking-[-0.03em] text-grey10 dark:text-white">9. International Data Transfers</h2>
                            <p className="text-[17px] leading-[1.75] text-grey40 dark:text-nightMuted">
                                Your information may be transferred to and processed in countries other than your own.
                                We ensure that such transfers comply with applicable data protection laws and implement
                                appropriate safeguards to protect your information.
                            </p>
                        </section>

                        <section className="mb-14">
                            <h2 className="mb-5 text-[clamp(24px,3vw,30px)] font-bold leading-tight tracking-[-0.03em] text-grey10 dark:text-white">10. Children's Privacy</h2>
                            <p className="text-[17px] leading-[1.75] text-grey40 dark:text-nightMuted">
                                Our services are not intended for children under 13 years of age. We do not knowingly
                                collect personal information from children under 13. If we become aware that we have
                                collected personal information from a child under 13, we will take steps to delete
                                such information promptly.
                            </p>
                        </section>

                        <section className="mb-14">
                            <h2 className="mb-5 text-[clamp(24px,3vw,30px)] font-bold leading-tight tracking-[-0.03em] text-grey10 dark:text-white">11. Changes to This Privacy Policy</h2>
                            <p className="text-[17px] leading-[1.75] text-grey40 dark:text-nightMuted">
                                We may update this Privacy Policy from time to time to reflect changes in our practices
                                or applicable laws. We will notify you of any material changes by posting the updated
                                policy on our website and updating the "Last updated" date. Your continued use of our
                                services after such changes constitutes acceptance of the updated policy.
                            </p>
                        </section>

                        <section className="mb-14">
                            <h2 className="mb-5 text-[clamp(24px,3vw,30px)] font-bold leading-tight tracking-[-0.03em] text-grey10 dark:text-white">12. Contact Us</h2>
                            <p className="mb-4 text-[17px] leading-[1.75] text-grey40 dark:text-nightMuted">
                                If you have any questions, concerns, or requests regarding this Privacy Policy or our
                                data practices, please contact us:
                            </p>
                            <div className={`${surface} p-6 sm:p-8`}>
                                <div className="space-y-3">
                                    <div>
                                        <strong className="font-semibold text-grey10 dark:text-white">Email:</strong>
                                        <span className="ml-2 break-words text-grey40 dark:text-nightMuted">admin@nexalogix.com</span>
                                    </div>
                                    <div>
                                        <strong className="font-semibold text-grey10 dark:text-white">Phone:</strong>
                                        <span className="ml-2 break-words text-grey40 dark:text-nightMuted">(949) 431-6160</span>
                                    </div>
                                    <div>
                                        <strong className="font-semibold text-grey10 dark:text-white">Address:</strong>
                                        <span className="ml-2 break-words text-grey40 dark:text-nightMuted">Aliso Viejo, CA 92656</span>
                                    </div>
                                </div>
                            </div>
                        </section>
                    </article>

                    <div className="mt-16">
                        <a
                            href="/"
                            className={btnPrimary}
                        >
                            <ArrowLeft className="h-4 w-4" />
                            Back to Home
                        </a>
                    </div>
                </div>
            </main>

            <Footer hasMobileNav />
            <MobileBottomNav />
        </div>
    );
}
